import React from 'react';
import { Link, useParams } from 'react-router';
import html2pdf from 'html2pdf.js';
import {
    Backdrop,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
    Pagination,
    Paper,
    Stack,
    styled,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import GridLayout from '../../components/GridLayout/GridLayout';
import MenuMoreVert from '../../components/Menu/MoreVert';
import { formatDate, padWithZeros } from '../../assets/utils/utils';
import vittiLogisticLog from '../../assets/imgs/vitti-logistics-logo.png';
import api from '../../api/api';

const StylizedChip = styled(Chip)(({ theme }) => ({
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    width: '100%',
}));

const StylizedPolicyContainer = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    height: '100%',
    overflowY: 'auto',
    padding: theme.shape.padding,
    width: '100%',
    whiteSpace: 'pre-line',
}));

const StylizedImage = styled('img')(({ theme }) => ({
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    margin: 'auto',
}));

const STATUS = {
    1: { label: 'Firmada', color: 'success' },
    0: { label: 'Pendiente', color: 'default' },
};

const UsersPerPolicy = () => {
    const { id, } = useParams();
    const printReference = React.useRef();
    const [loading, setLoading] = React.useState(true);
    const [title, setTitle] = React.useState('');
    const [usersByPolicy, setUsersByPolicy] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [dialogProps, setDialogProps] = React.useState({
        open: false,
        title: '',
        content: '',
        file: '',
        user: '',
        date: '',
    });

    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleOpenDialog = React.useCallback((data) => {
        setDialogProps(prevProps => ({ ...prevProps, open: true, data: { ...data }, }));
    }, []);

    const handleCloseDialog = React.useCallback(() => {
        setDialogProps(prevProps => ({ ...prevProps, open: false }));
    }, []);

    const handleGeneratePDF = (documentName) => {
        printReference.current.querySelector(".ri-printer-line").style.display = 'none';
        const opciones = {
            margin:       10.5,
            filename:     `${documentName}.pdf`,
            image:        { type: 'jpeg', quality: 0.98 },
            html2canvas:  { scale: 2 },
            jsPDF:        { unit: 'mm', format: 'letter', orientation: 'portrait' }
        };

        html2pdf().set(opciones).from(printReference.current).save().then(() => {
            printReference.current.querySelector(".ri-printer-line").style.removeProperty('display');
        });
    
    }

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const { ok, data } = await api.get(`policies/all_users_by_id/?id=${id}&page=${page}`);
                if (ok) {
                    setTitle(data[0]?.policy);
                    setUsersByPolicy(data);
                }
            }
            catch (error) {
                console.log('Error al cargar la información: ', error);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, page]);

    if (loading) return (<Backdrop open={loading} invisible><CircularProgress /></Backdrop>);

    return (
        <GridLayout>
            <Grid size={12}>
                <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>{title && title.toUpperCase()}</Typography>
                <Stack direction="row" sx={{ justifyContent: 'center', alignItems: 'center', }}>
                    {!isMediumScreen &&
                        <Breadcrumbs sx={{ width: '100%', }}>
                            <Link to={'/politicas-empresa'}>
                                <Typography variant="body2" color="primary">Politicas</Typography>
                            </Link>
                            <Typography variant="body2" color="textSecondary">{title}</Typography>
                        </Breadcrumbs>}
                </Stack>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {['NOMBRE', 'USUARIO', 'PUESTO', 'FECHA DE ASIGNACIÓN', 'ESTATUS', 'ACCIONES'].map((header) => (
                                    <TableCell key={header} align="center">
                                        <Typography variant="body1" fontWeight="bold" color="primary">
                                            {header}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {usersByPolicy.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight="bold" color="primary">{row.user_full_name}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight="bold" color="primary">{row.username}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight="bold" color="primary">{row.job_position}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight="bold" color="primary">{formatDate(row.created_at)}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <StylizedChip label={STATUS[row.signing_date ? 1 : 0]?.label} color={STATUS[row.signing_date ? 1 : 0]?.color} sx={{ width: '100%', }} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <MenuMoreVert options={[
                                            {
                                                label: 'Ver Política',
                                                onClick: () => handleOpenDialog(row),
                                                disabled: !row.signing_date,
                                            },
                                        ]} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Pagination
                    count={(usersByPolicy[0] && parseInt(usersByPolicy[0]['total_pages'])) || 1}
                    color="primary"
                    page={page}
                    onChange={(e, page) => setPage(page)}
                    sx={{ mt: 2, float: 'right', }} />
            </Grid>
            <Dialog id="main" open={dialogProps.open} onClose={handleCloseDialog} slotProps={{
                paper: {
                    ref: printReference,
                    elevation: 0,
                    sx: { height: '100%', maxWidth: "800px !important" },
                }
            }}>
                <DialogTitle variant="h6" fontWeight="bold" color="primary" style={{ textAlign: 'center', paddingBottom: 30}}>
                    <img src={vittiLogisticLog} alt="" width="250" />
                    <Typography variant="body1" color="primary" fontWeight="bold" textAlign={"center"} style={{ marginTop: '10px' }}>
                        {dialogProps?.data?.policy.toUpperCase()}
                        <i id="print-icon" className="ri-printer-line" style={{ cursor: 'pointer', float: 'right' }}
                        onClick={() => handleGeneratePDF(dialogProps?.data?.policy.toUpperCase())}
                        // onClick={() => window.print()}
                        title="Generar PDF"></i>
                    </Typography>
                    <Grid container mt={2} style={{ textAlign: 'left', padding: "10px 0px" }}>
                        <Grid size={6}>
                            <Typography variant="body2" color="primary"><b>FECHA DE EMISIÓN:</b> {formatDate(dialogProps?.data?.created_at)}</Typography>
                        </Grid>
                        <Grid size={6}>
                            <Typography variant="body2" color="primary" textAlign="right"><b>FECHA DE REVISIÓN:</b> {formatDate(dialogProps?.data?.created_at)}</Typography>
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="body2" color="primary"><b>CÓDIGO:</b> VLP-{padWithZeros(dialogProps?.data?.pk_policy_id || 0, 4)}</Typography>
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="body2" color="primary"><b>VERSIÓN:</b> 1</Typography>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent id="dialog-content" style={{ overflow: 'visible', }}>
                    <StylizedPolicyContainer style={{ border: 'none', height: 'fit-content' }} id="dialog-content-text" component="div" variant="body2" dangerouslySetInnerHTML={{ __html: dialogProps?.data?.content + dialogProps?.data?.content }} />
                </DialogContent>
                <DialogActions className='hello'>
                    <Stack direction="column" textAlign="center" margin="auto">
                        <StylizedImage src={`data:image/png;base64,${dialogProps?.data?.signature_file}`} alt="Firma" width={150} style={{ border: 'none' }} />
                        <Typography variant="body2" color="primary" fontWeight="bold" mt={1} style={{ borderTop: "1px solid", marginTop: 0, minWidth: 150 }}>
                            {dialogProps?.data?.user_full_name}
                        </Typography>
                        <Typography variant="body2" color="primary">{formatDate(dialogProps?.data?.signing_date)}</Typography>
                    </Stack>
                </DialogActions>
            </Dialog>
        </GridLayout>
    )
};

export default UsersPerPolicy;